import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import MainTable from 'app/views/components/Table/MainTable'
import getColumnDefs, { assignmentsForExport } from './trade-closing-columnDefs'
import { loadAssignmentList } from 'app/state/ducks/assignment-list/thunks'
import {
  getAssignmentList,
  getAssignmentListLoader,
  isAssignmentListStale,
} from 'app/state/ducks/assignment-list/selectors'
import { AssignmentListItem } from 'app/models/assignment-list-params'
import { MRT_ColumnDef } from 'mantine-react-table'
import { useNavigate } from 'react-router-dom'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { getKPIData } from './trade-closing-kpi-data'
import { Group, Stack } from '@mantine/core'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import PrimaryInput from 'app/views/components/buttons/PrimaryInput'
import {
  GridSortingStorage,
  GridFilterStorage,
  GridPaginationStorage,
  GridColumnVisibilityStorage,
  useGridSorting,
  useGridFilters,
  useGridPagination,
  useGridColumnVisibility,
} from 'app/utils/grid-sorting-storage'

export function TradeClosingDrillPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  function navigateToTradeClosingNew() {
    navigate('/tradeclosing/new-assignment')
  }

  // ONE request for the screen (ADR_LOS-030): the rows arrive with the deal
  // and facility names, both sides' entity names, the currency and the summed
  // amount already resolved, so the page loads no tenant-wide list.
  const assignmentList = useSelector(getAssignmentList)
  const columnDefs: MRT_ColumnDef<AssignmentListItem>[] = useMemo(
    () => getColumnDefs(),
    [],
  )

  const [filteredTableData, setFilteredTableData] = useState<AssignmentListItem[]>([])
  // the grid's own export writes "[object Object]" for the party columns
  const exportData = useMemo(() => assignmentsForExport(filteredTableData), [filteredTableData])
  const kpiData = getKPIData(assignmentList, setFilteredTableData)

  const assignmentLoader = useSelector(getAssignmentListLoader)
  const stale = useSelector(isAssignmentListStale)
  const [globalLoading, setGlobalLoading] = useState(false)

  // Grid preferences hooks
  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.tradeClosing,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.tradeClosing,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.tradeClosing)
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(
    GridFilterStorage.tradeClosing,
  )

  useEffect(() => {
    setGlobalLoading(assignmentLoader)
  }, [assignmentLoader])

  useEffect(() => {
    dispatch(loadAssignmentList() as any)
  }, [dispatch])

  // A write the rows could not be patched for: reload once, while on screen.
  useEffect(() => {
    if (stale && !assignmentLoader) dispatch(loadAssignmentList() as any)
  }, [stale, assignmentLoader])

  useEffect(() => {
    setFilteredTableData(assignmentList)
  }, [assignmentList])

  function handleRowClicked(row: AssignmentListItem) {
    navigate(`/tradeclosing/assignment/` + row.status + `/` + row.id + ``)
  }

  const tabs = {
    tradeClosing: { route: '/tradeclosing', label: 'ALL TRADES' },
    tradeClosingParent: { route: '/tradeclosing/parent', label: 'PARENT' },
    tradeClosingSubTrade: { route: '/tradeclosing/subtrade', label: 'SUB TRADE' },
  }

  return (
    <Stack>
      <Group w="100%" position="apart" className="tabsGapsActivity">
        <Group position="right">
          <SegmentControlUpdated tabs={tabs} />
        </Group>
        <Group noWrap>
          <PrimaryInput
            onClick={navigateToTradeClosingNew}
            text="Create Assignment"
          />
        </Group>
      </Group>
      <KPI_Section kpiData={kpiData} isLoading={globalLoading} />
      <MainTable
          tableName="Trade_Closing Table"
          withPagination={true}
          csvExportEnabled={true}
          customExportData={exportData}
          enableTopToolbar={false}
          headerBackgroundColor="#F0EEEE"
          overflow="auto"
          minHeight="0px"
          isLoading={globalLoading}
          columnDefs={columnDefs}
          data={filteredTableData}
          initialSorting={[
            { id: sorting.sortBy, desc: sorting.order === 'desc' },
          ]}
          onSortingChange={handleSortingChange}
          initialColumnFilters={toMRTColumnFilters()}
          onColumnFiltersChange={handleMRTFiltersChange}
          initialPagination={pagination}
          onPaginationChange={handlePaginationChange}
          initialColumnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          setRow={(row: AssignmentListItem, cellName: string) => {
            if (cellName != 'id') {
              handleRowClicked(row)
            }
          }}
      />
    </Stack>
  )
}
